import { useState, useEffect, useCallback } from 'react';
import { appointmentsAPI, schedulesAPI, patientsAPI, rescheduleAPI } from '../lib/api';
import { useAuth } from '../lib/auth';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import {
  CalendarPlus, Clock, Users, CheckCircle, XCircle, AlertTriangle,
  Settings2, ChevronRight, Search, RefreshCw, UserCheck, Play, Ban, Calendar, Undo2,
} from 'lucide-react';
import TouchTimePicker from '../components/TouchTimePicker';

const STATUS_COLORS = {
  scheduled: 'bg-blue-100 text-blue-700',
  checked_in: 'bg-amber-100 text-amber-700',
  in_progress: 'bg-purple-100 text-purple-700',
  completed: 'bg-emerald-100 text-emerald-700',
  cancelled: 'bg-red-100 text-red-600',
  no_show: 'bg-slate-200 text-slate-600',
};

const TYPE_COLORS = {
  consultation: 'bg-blue-50 text-blue-600',
  follow_up: 'bg-purple-50 text-purple-600',
  procedure: 'bg-amber-50 text-amber-600',
  emergency: 'bg-red-50 text-red-600',
};

const DAYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
const DAY_LABELS = { monday: 'Mon', tuesday: 'Tue', wednesday: 'Wed', thursday: 'Thu', friday: 'Fri', saturday: 'Sat', sunday: 'Sun' };

// Common time slots for picker
const TIME_PRESETS = [
  { label: 'Morning', start: '09:00', end: '13:00' },
  { label: 'Afternoon', start: '14:00', end: '17:00' },
  { label: 'Evening', start: '17:00', end: '20:00' },
  { label: 'Full Day', start: '09:00', end: '18:00' },
];

// =========== Schedule Config Dialog ===========
const ScheduleDialog = ({ open, onOpenChange, doctors }) => {
  const [selectedDoctor, setSelectedDoctor] = useState('');
  const [schedules, setSchedules] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedDays, setSelectedDays] = useState(new Set());
  const [startTime, setStartTime] = useState('09:00');
  const [endTime, setEndTime] = useState('17:00');
  const [slotMinutes, setSlotMinutes] = useState(15);

  useEffect(() => {
    if (!open) {
      setSelectedDoctor(''); setSchedules([]); setSelectedDays(new Set());
      setStartTime('09:00'); setEndTime('17:00'); setSlotMinutes(15);
    }
  }, [open]);

  const loadSchedules = async (docId) => {
    if (!docId) return;
    setLoading(true);
    try {
      const res = await schedulesAPI.list({ doctor_id: docId });
      setSchedules(res.data || []);
    } catch { toast.error('Failed to load schedules'); }
    setLoading(false);
  };

  useEffect(() => { if (selectedDoctor) loadSchedules(selectedDoctor); }, [selectedDoctor]);

  const toggleDay = (d) => {
    setSelectedDays(prev => {
      const next = new Set(prev);
      if (next.has(d)) next.delete(d); else next.add(d);
      return next;
    });
  };

  const applyPreset = (preset) => {
    if (preset === 'weekdays') setSelectedDays(new Set(['monday', 'tuesday', 'wednesday', 'thursday', 'friday']));
    else if (preset === 'weekends') setSelectedDays(new Set(['saturday', 'sunday']));
    else if (preset === 'all') setSelectedDays(new Set(DAYS));
    else if (preset === 'clear') setSelectedDays(new Set());
  };

  const applyTimePreset = (p) => { setStartTime(p.start); setEndTime(p.end); };

  const handleSave = async () => {
    if (!selectedDoctor) { toast.error('Pick a doctor'); return; }
    if (selectedDays.size === 0) { toast.error('Select at least one day'); return; }
    if (startTime >= endTime) { toast.error('End time must be after start time'); return; }
    try {
      // Save each selected day. If a schedule for that day already exists, update; else create.
      const dayList = Array.from(selectedDays);
      const results = await Promise.allSettled(
        dayList.map(d => {
          const existing = schedules.find(s => s.day_of_week === d);
          const payload = {
            doctor_id: selectedDoctor,
            day_of_week: d,
            start_time: startTime,
            end_time: endTime,
            slot_duration_minutes: slotMinutes,
            is_active: true,
          };
          return existing
            ? schedulesAPI.update(existing.id, payload)
            : schedulesAPI.create(payload);
        })
      );
      const ok = results.filter(r => r.status === 'fulfilled').length;
      toast.success(`Saved ${ok} day${ok > 1 ? 's' : ''} of availability`);
      loadSchedules(selectedDoctor);
      setSelectedDays(new Set());
    } catch (e) {
      toast.error('Failed to save schedule');
    }
  };

  const handleDelete = async (scheduleId) => {
    if (!window.confirm('Remove this availability slot?')) return;
    try {
      await schedulesAPI.delete(scheduleId);
      toast.success('Removed');
      loadSchedules(selectedDoctor);
    } catch { toast.error('Failed to remove'); }
  };

  const doctor = doctors.find(d => d.id === selectedDoctor);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Configure Doctor Availability</DialogTitle>
        </DialogHeader>
        <div className="space-y-5 pt-2">
          {/* Doctor select (shadcn — same UX as everywhere else) */}
          <div>
            <Label className="text-sm font-medium">Doctor *</Label>
            <Select value={selectedDoctor} onValueChange={setSelectedDoctor}>
              <SelectTrigger className="h-11 mt-1.5" data-testid="schedule-doctor-select">
                <SelectValue placeholder="Choose a doctor..." />
              </SelectTrigger>
              <SelectContent className="max-h-[300px]">
                {doctors.length === 0 ? (
                  <div className="p-3 text-sm text-slate-400 text-center">No doctors found. Onboard a doctor first.</div>
                ) : doctors.map(d => (
                  <SelectItem key={d.id} value={d.id}>
                    <div className="flex items-center gap-2">
                      <span className="font-medium">{d.first_name} {d.last_name}</span>
                      {d.specialization && <span className="text-xs text-slate-400">· {d.specialization}</span>}
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {selectedDoctor && (
            <>
              {/* Existing schedules preview */}
              <div className="bg-slate-50/50 rounded-xl p-4">
                <h4 className="text-xs uppercase font-semibold text-slate-500 mb-2">Current Availability {doctor && `· ${doctor.first_name} ${doctor.last_name}`}</h4>
                {loading ? <p className="text-slate-400 text-sm">Loading...</p> : (
                  schedules.length === 0 ? (
                    <p className="text-sm text-slate-400 italic">No schedule configured yet — add one below.</p>
                  ) : (
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
                      {schedules.map(s => (
                        <div key={s.id} className="bg-white rounded-lg border border-slate-200 px-3 py-2 text-sm flex items-center justify-between group" data-testid={`schedule-${s.id}`}>
                          <div>
                            <p className="font-medium capitalize">{s.day_of_week.slice(0, 3)}</p>
                            <p className="text-xs text-slate-500">{s.start_time} - {s.end_time}</p>
                          </div>
                          <button onClick={() => handleDelete(s.id)} className="opacity-0 group-hover:opacity-100 transition text-red-400 hover:text-red-600" data-testid={`delete-schedule-${s.id}`}>
                            <XCircle size={16} />
                          </button>
                        </div>
                      ))}
                    </div>
                  )
                )}
              </div>

              {/* Add new — Multi-day picker */}
              <div className="space-y-3 bg-white rounded-xl border border-slate-200 p-4">
                <h4 className="text-sm font-semibold text-slate-700">Add Availability</h4>

                {/* Quick day presets */}
                <div className="flex gap-1.5 flex-wrap">
                  <button type="button" onClick={() => applyPreset('weekdays')} className="text-xs px-2.5 py-1 rounded-md bg-teal-50 text-teal-700 hover:bg-teal-100 font-medium" data-testid="preset-weekdays">Weekdays</button>
                  <button type="button" onClick={() => applyPreset('weekends')} className="text-xs px-2.5 py-1 rounded-md bg-purple-50 text-purple-700 hover:bg-purple-100 font-medium" data-testid="preset-weekends">Weekends</button>
                  <button type="button" onClick={() => applyPreset('all')} className="text-xs px-2.5 py-1 rounded-md bg-slate-100 text-slate-700 hover:bg-slate-200 font-medium" data-testid="preset-all-days">All Days</button>
                  <button type="button" onClick={() => applyPreset('clear')} className="text-xs px-2.5 py-1 rounded-md bg-red-50 text-red-600 hover:bg-red-100 font-medium" data-testid="preset-clear-days">Clear</button>
                </div>

                {/* Day chips */}
                <div>
                  <Label className="text-xs text-slate-600">Days (multi-select)</Label>
                  <div className="flex gap-1.5 mt-1.5 flex-wrap">
                    {DAYS.map(d => {
                      const active = selectedDays.has(d);
                      return (
                        <button key={d} type="button" onClick={() => toggleDay(d)}
                          className={`min-w-[44px] h-10 rounded-lg text-sm font-semibold transition ${
                            active
                              ? 'bg-teal-600 text-white shadow-sm scale-105'
                              : 'bg-slate-50 text-slate-600 hover:bg-slate-100 border border-slate-200'
                          }`}
                          data-testid={`day-chip-${d}`}>
                          {DAY_LABELS[d]}
                        </button>
                      );
                    })}
                  </div>
                  {selectedDays.size > 0 && (
                    <p className="text-xs text-teal-600 mt-1.5">{selectedDays.size} day{selectedDays.size > 1 ? 's' : ''} selected</p>
                  )}
                </div>

                {/* Time presets */}
                <div>
                  <Label className="text-xs text-slate-600">Quick time presets</Label>
                  <div className="flex gap-1.5 mt-1.5 flex-wrap">
                    {TIME_PRESETS.map(p => (
                      <button key={p.label} type="button" onClick={() => applyTimePreset(p)}
                        className="text-xs px-2.5 py-1.5 rounded-md bg-slate-50 hover:bg-teal-50 hover:text-teal-700 border border-slate-200 text-slate-700 font-medium transition"
                        data-testid={`time-preset-${p.label.toLowerCase().replace(' ', '-')}`}>
                        {p.label} ({p.start}–{p.end})
                      </button>
                    ))}
                  </div>
                </div>

                {/* Time + slot inputs */}
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <Label className="text-xs text-slate-600">Start time</Label>
                    <TouchTimePicker value={startTime} onChange={setStartTime} className="mt-1" testId="start-time-input" />
                  </div>
                  <div>
                    <Label className="text-xs text-slate-600">End time</Label>
                    <TouchTimePicker value={endTime} onChange={setEndTime} className="mt-1" testId="end-time-input" />
                  </div>
                  <div>
                    <Label className="text-xs text-slate-600">Slot duration</Label>
                    <Select value={String(slotMinutes)} onValueChange={v => setSlotMinutes(parseInt(v))}>
                      <SelectTrigger className="h-10 mt-1" data-testid="slot-duration-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {[10, 15, 20, 30, 45, 60].map(m => <SelectItem key={m} value={String(m)}>{m} min</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <Button onClick={handleSave} className="w-full bg-teal-600 hover:bg-teal-700 mt-2" data-testid="save-schedule-btn">
                  <CheckCircle size={16} className="mr-1.5" /> Save Availability
                </Button>
              </div>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

// =========== Book Appointment Dialog ===========
const BookAppointmentDialog = ({ open, onOpenChange, doctors, onCreated }) => {
  const [step, setStep] = useState(1);
  const [form, setForm] = useState({ patient_id: '', doctor_id: '', appointment_date: new Date().toISOString().split('T')[0], time_slot: '', appointment_type: 'consultation', chief_complaint: '' });
  const [patients, setPatients] = useState([]);
  const [patientSearch, setPatientSearch] = useState('');
  const [slots, setSlots] = useState(null);
  const [loading, setLoading] = useState(false);
  // Item 7 — cascading Department → Doctor
  const [department, setDepartment] = useState('');
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  // Unique departments derived from loaded doctors (single source of truth — already loaded)
  const departments = Array.from(new Set(doctors.map(d => d.department).filter(Boolean))).sort();
  const filteredDoctors = department ? doctors.filter(d => d.department === department) : doctors;

  useEffect(() => {
    if (patientSearch.length >= 2) {
      patientsAPI.list({ search: patientSearch }).then(r => setPatients(r.data)).catch(() => {});
    }
  }, [patientSearch]);

  // Reset doctor when department changes if previously selected doctor is no longer in the filtered list
  useEffect(() => {
    if (form.doctor_id && department && !filteredDoctors.find(d => d.id === form.doctor_id)) {
      set('doctor_id', '');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [department]);

  useEffect(() => {
    if (form.doctor_id && form.appointment_date) {
      appointmentsAPI.availableSlots(form.doctor_id, form.appointment_date).then(r => setSlots(r.data)).catch(() => setSlots(null));
    }
  }, [form.doctor_id, form.appointment_date]);

  // Item 4 — local-TZ past-slot check (in addition to server's past_slots). When the
  // selected date is today (local), any slot whose time is before now-local is marked past.
  const localPastSet = (() => {
    const set = new Set();
    const today = new Date();
    const localToday = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
    if (form.appointment_date !== localToday) return set;
    const nowMins = today.getHours() * 60 + today.getMinutes();
    const allSlots = (slots && (slots.all_slots || slots.slots)) || [];
    allSlots.forEach(s => {
      const [h, m] = (s || '0:0').split(':').map(Number);
      if (h * 60 + m <= nowMins) set.add(s);
    });
    return set;
  })();

  const handleSubmit = async () => {
    if (!form.patient_id || !form.doctor_id || !form.time_slot) { toast.error('Fill all required fields'); return; }
    setLoading(true);
    try {
      await appointmentsAPI.create(form);
      toast.success('Appointment booked successfully');
      const bookedDate = form.appointment_date;
      onOpenChange(false);
      setStep(1);
      setForm({ patient_id: '', doctor_id: '', appointment_date: new Date().toISOString().split('T')[0], time_slot: '', appointment_type: 'consultation', chief_complaint: '' });
      setDepartment('');
      // Jump the parent's date filter to the booked date so user sees it immediately
      onCreated?.(bookedDate);
    } catch (e) { toast.error(e.response?.data?.detail || 'Booking failed'); }
    finally { setLoading(false); }
  };

  const selectedPatient = patients.find(p => p.id === form.patient_id);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-xl" data-testid="book-appointment-dialog">
        <DialogHeader><DialogTitle>Book Appointment</DialogTitle></DialogHeader>
        <div className="space-y-4">
          {step === 1 && (
            <>
              <div>
                <Label>Search Patient *</Label>
                <Input placeholder="Type name, ID or phone..." value={patientSearch} onChange={e => setPatientSearch(e.target.value)} data-testid="appointment-patient-search" />
                {patients.length > 0 && !form.patient_id && (
                  <div className="border rounded-lg mt-1 max-h-36 overflow-y-auto">
                    {patients.map(p => (
                      <button key={p.id} onClick={() => { set('patient_id', p.id); setPatientSearch(`${p.first_name} ${p.last_name}`); }}
                        className="w-full text-left px-3 py-2 text-sm hover:bg-emerald-50 flex items-center justify-between" data-testid={`patient-option-${p.id}`}>
                        <span>{p.first_name} {p.last_name}</span>
                        <span className="text-xs text-slate-400 font-mono">{p.patient_id}</span>
                      </button>
                    ))}
                  </div>
                )}
                {selectedPatient && <p className="text-sm text-emerald-600 mt-1">Selected: {selectedPatient.first_name} {selectedPatient.last_name} ({selectedPatient.patient_id})</p>}
              </div>
              <div>
                <Label>Department *</Label>
                <Select value={department || 'all'} onValueChange={v => setDepartment(v === 'all' ? '' : v)}>
                  <SelectTrigger data-testid="appointment-department-select"><SelectValue placeholder="All departments" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All departments</SelectItem>
                    {departments.map(dept => <SelectItem key={dept} value={dept}>{dept}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Doctor *</Label>
                <Select value={form.doctor_id} onValueChange={v => set('doctor_id', v)}>
                  <SelectTrigger data-testid="appointment-doctor-select"><SelectValue placeholder={department ? `Select doctor from ${department}` : 'Select doctor'} /></SelectTrigger>
                  <SelectContent>
                    {filteredDoctors.length === 0 ? (
                      <div className="p-3 text-sm text-slate-400 text-center">No doctors{department ? ` in ${department}` : ''}</div>
                    ) : filteredDoctors.map(d => (
                      <SelectItem key={d.id} value={d.id}>
                        {d.first_name} {d.last_name}{d.specialization ? ` · ${d.specialization}` : ''}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Date *</Label><Input type="date" value={form.appointment_date} min={new Date().toISOString().slice(0,10)} onChange={e => set('appointment_date', e.target.value)} data-testid="appointment-date-input" /></div>
                <div><Label>Type</Label>
                  <Select value={form.appointment_type} onValueChange={v => set('appointment_type', v)}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {['consultation', 'follow_up', 'procedure', 'emergency'].map(t => <SelectItem key={t} value={t}>{t.replace('_', ' ')}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <Button onClick={() => setStep(2)} disabled={!form.patient_id || !form.doctor_id} className="w-full bg-emerald-600 hover:bg-emerald-700" data-testid="appointment-next-btn">
                Next: Select Slot <ChevronRight size={16} className="ml-1" />
              </Button>
            </>
          )}

          {step === 2 && (
            <>
              <Button variant="ghost" size="sm" onClick={() => setStep(1)}>Back</Button>
              {!slots ? (
                <p className="text-center text-slate-400 py-6">Loading slots...</p>
              ) : (!slots.all_slots || slots.all_slots.length === 0) && (slots.slots?.length === 0) ? (
                <div className="text-center py-6">
                  <AlertTriangle size={32} className="mx-auto text-amber-400 mb-2" />
                  <p className="text-slate-500">{slots.message || 'No available slots for this date'}</p>
                </div>
              ) : (
                <>
                  <p className="text-sm text-slate-600">
                    Available slots ({slots.slot_duration_minutes} min each) for <span className="font-medium">{form.appointment_date}</span>
                  </p>
                  <div className="flex flex-wrap gap-3 text-[10px] text-slate-500 mb-1">
                    <span className="flex items-center gap-1"><span className="inline-block h-2.5 w-2.5 rounded bg-emerald-500" /> Available</span>
                    <span className="flex items-center gap-1"><span className="inline-block h-2.5 w-2.5 rounded bg-slate-300" /> Already booked</span>
                    <span className="flex items-center gap-1"><span className="inline-block h-2.5 w-2.5 rounded bg-slate-200" /> Past</span>
                  </div>
                  <div className="grid grid-cols-4 gap-2 max-h-52 overflow-y-auto">
                    {(slots.all_slots || slots.slots).map(s => {
                      const isPast = (slots.past_slots || []).includes(s) || localPastSet.has(s);
                      const isBooked = (slots.booked_slots || []).includes(s);
                      const isBlocked = (slots.blocked_slots || []).includes(s);
                      const disabled = isPast || isBooked || isBlocked;
                      const selected = form.time_slot === s;
                      let cls = 'border-slate-200 hover:border-emerald-400 hover:bg-emerald-50';
                      let title = 'Available';
                      if (selected) { cls = 'bg-emerald-600 text-white border-emerald-600 shadow-md'; title = 'Selected'; }
                      else if (isPast) { cls = 'bg-slate-100 text-slate-400 border-slate-200 line-through cursor-not-allowed'; title = 'Past — cannot book'; }
                      else if (isBooked) { cls = 'bg-slate-200 text-slate-500 border-slate-300 cursor-not-allowed'; title = 'Already booked'; }
                      else if (isBlocked) { cls = 'bg-amber-100 text-amber-700 border-amber-200 cursor-not-allowed'; title = 'Doctor unavailable'; }
                      return (
                        <button
                          key={s}
                          onClick={() => !disabled && set('time_slot', s)}
                          disabled={disabled}
                          title={title}
                          className={`px-3 py-2 rounded-lg border text-sm font-medium transition-all ${cls}`}
                          data-testid={`slot-${s}`}
                        >
                          {s}
                        </button>
                      );
                    })}
                  </div>
                  <div><Label>Chief Complaint</Label><Textarea value={form.chief_complaint} onChange={e => set('chief_complaint', e.target.value)} rows={2} data-testid="appointment-complaint" /></div>
                  <Button onClick={handleSubmit} disabled={!form.time_slot || loading} className="w-full bg-emerald-600 hover:bg-emerald-700" data-testid="confirm-booking-btn">
                    {loading ? 'Booking...' : 'Confirm Booking'}
                  </Button>
                </>
              )}
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

// =========== Reschedule Dialog ===========
const RescheduleDialog = ({ open, onOpenChange, apt, onDone }) => {
  const [newDate, setNewDate] = useState('');
  const [newTime, setNewTime] = useState('');
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open && apt) { setNewDate(apt.date || ''); setNewTime(apt.time || ''); setReason(''); }
  }, [open, apt]);

  const save = async () => {
    if (!newDate || !newTime) { toast.error('Pick new date and time'); return; }
    setSaving(true);
    try {
      await rescheduleAPI.reschedule(apt.id, { new_date: newDate, new_time: newTime, reason });
      toast.success('Appointment rescheduled');
      onDone(); onOpenChange(false);
    } catch { toast.error('Failed'); }
    setSaving(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle>Reschedule Appointment</DialogTitle></DialogHeader>
        <div className="space-y-3 mt-2">
          <div className="text-sm text-slate-600 bg-slate-50 rounded p-3">
            <p><strong>Patient:</strong> {apt?.patient_name}</p>
            <p><strong>Current:</strong> {apt?.date} at {apt?.time}</p>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>New Date *</Label><Input type="date" value={newDate} onChange={e => setNewDate(e.target.value)} data-testid="reschedule-date" /></div>
            <div><Label>New Time *</Label><TouchTimePicker value={newTime} onChange={setNewTime} testId="reschedule-time" /></div>
          </div>
          <div><Label>Reason</Label><Input value={reason} onChange={e => setReason(e.target.value)} placeholder="Patient request, doctor unavailable..." /></div>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button onClick={save} disabled={saving} className="bg-teal-600 hover:bg-teal-700" data-testid="confirm-reschedule-btn">{saving ? 'Saving...' : 'Reschedule'}</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// =========== Status Action Buttons ===========
const StatusActions = ({ apt, onUpdate, onReschedule, onUndo, isDoctor }) => {
  // Defensive: backend used to set status='rescheduled' on reschedule, which left rows stuck without Check-In.
  // We now treat 'rescheduled' identically to 'scheduled' so legacy rows still show the Check-In path.
  const transitions = {
    scheduled: [{ label: 'Check In', status: 'checked_in', icon: UserCheck, color: 'bg-amber-500 hover:bg-amber-600' }, { label: 'Cancel', status: 'cancelled', icon: Ban, color: 'bg-red-500 hover:bg-red-600' }, { label: 'No Show', status: 'no_show', icon: XCircle, color: 'bg-slate-500 hover:bg-slate-600' }],
    rescheduled: [{ label: 'Check In', status: 'checked_in', icon: UserCheck, color: 'bg-amber-500 hover:bg-amber-600' }, { label: 'Cancel', status: 'cancelled', icon: Ban, color: 'bg-red-500 hover:bg-red-600' }, { label: 'No Show', status: 'no_show', icon: XCircle, color: 'bg-slate-500 hover:bg-slate-600' }],
    // Item 2 — Doctor sees ONLY "Consultation Completed" after Check-In. We collapse the prior
    // two-step "Start" → "Complete" into a single mandatory action so the queue doesn't get stuck.
    checked_in: isDoctor
      ? [{ label: 'Consultation Completed', status: 'completed', icon: CheckCircle, color: 'bg-emerald-600 hover:bg-emerald-700' }, { label: 'Cancel', status: 'cancelled', icon: Ban, color: 'bg-red-500 hover:bg-red-600' }]
      : [{ label: 'Cancel', status: 'cancelled', icon: Ban, color: 'bg-red-500 hover:bg-red-600' }],
    in_progress: isDoctor
      ? [{ label: 'Consultation Completed', status: 'completed', icon: CheckCircle, color: 'bg-emerald-600 hover:bg-emerald-700' }, { label: 'Cancel', status: 'cancelled', icon: Ban, color: 'bg-red-500 hover:bg-red-600' }]
      : [],
  };
  const actions = transitions[apt.status] || [];
  const canReschedule = ['scheduled', 'rescheduled', 'checked_in'].includes(apt.status);
  // Undo button — only show when this appointment has actually been rescheduled at least once
  const canUndo = !!apt.last_rescheduled_at && ['scheduled', 'rescheduled', 'checked_in'].includes(apt.status);
  if (!actions.length && !canReschedule && !canUndo) return null;
  return (
    <div className="flex gap-1 flex-wrap">
      {canReschedule && (
        <Button size="sm" className="bg-blue-500 hover:bg-blue-600 text-white h-7 px-2 text-xs" onClick={(e) => { e.stopPropagation(); onReschedule(apt); }} data-testid={`action-reschedule-${apt.id}`}>
          <Calendar size={12} className="mr-1" /> Reschedule
        </Button>
      )}
      {canUndo && (
        <Button size="sm" variant="outline" className="border-slate-300 text-slate-700 hover:bg-slate-100 h-7 px-2 text-xs" onClick={(e) => { e.stopPropagation(); onUndo(apt); }} data-testid={`action-undo-${apt.id}`} title="Revert to previous slot">
          <Undo2 size={12} className="mr-1" /> Undo
        </Button>
      )}
      {actions.map(a => (
        <Button key={a.status} size="sm" className={`${a.color} text-white h-7 px-2 text-xs`} onClick={(e) => { e.stopPropagation(); onUpdate(apt.id, a.status); }} data-testid={`action-${a.status}-${apt.id}`}>
          <a.icon size={12} className="mr-1" /> {a.label}
        </Button>
      ))}
    </div>
  );
};

// =========== Main Page ===========
export const AppointmentsPage = () => {
  const { user } = useAuth();
  const isDoctor = (user?.role_name || '').toLowerCase() === 'doctor' || (user?.role_code || '').toLowerCase() === 'doctor' || user?.is_super_admin;
  const [appointments, setAppointments] = useState([]);
  const [counts, setCounts] = useState({});
  const [doctors, setDoctors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
  const [filterDoctor, setFilterDoctor] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [showBook, setShowBook] = useState(false);
  const [showSchedule, setShowSchedule] = useState(false);
  const [rescheduleApt, setRescheduleApt] = useState(null);

  const fetch = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (date) params.date = date;
      if (filterDoctor && filterDoctor !== 'all') params.doctor_id = filterDoctor;
      if (filterStatus && filterStatus !== 'all') params.status = filterStatus;
      const [aRes, cRes, dRes] = await Promise.all([
        appointmentsAPI.list(params),
        appointmentsAPI.count({ date }),
        patientsAPI.getDoctors(),
      ]);
      setAppointments(aRes.data);
      setCounts(cRes.data);
      setDoctors(dRes.data);
    } catch { toast.error('Failed to load appointments'); }
    finally { setLoading(false); }
  }, [date, filterDoctor, filterStatus]);

  useEffect(() => { fetch(); }, [fetch]);

  const handleStatusUpdate = async (aptId, newStatus) => {
    try {
      await appointmentsAPI.updateStatus(aptId, { status: newStatus });
      toast.success(`Status updated to ${newStatus.replace('_', ' ')}`);
      fetch();
    } catch (e) { toast.error(e.response?.data?.detail || 'Update failed'); }
  };

  const handleUndo = async (apt) => {
    try {
      const res = await rescheduleAPI.undo(apt.id);
      const t = res?.data?.reverted_to?.time || '';
      toast.success(`Reverted ${apt.patient_name || 'appointment'} to ${t}`);
      fetch();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to undo');
    }
  };

  const statCards = [
    { label: 'Total', key: 'total', icon: Users, color: 'bg-slate-100 text-slate-600', iconColor: 'text-slate-500' },
    { label: 'Scheduled', key: 'scheduled', icon: Clock, color: 'bg-blue-50 text-blue-700', iconColor: 'text-blue-500' },
    { label: 'Checked In', key: 'checked_in', icon: UserCheck, color: 'bg-amber-50 text-amber-700', iconColor: 'text-amber-500' },
    { label: 'In Progress', key: 'in_progress', icon: Play, color: 'bg-purple-50 text-purple-700', iconColor: 'text-purple-500' },
    { label: 'Completed', key: 'completed', icon: CheckCircle, color: 'bg-emerald-50 text-emerald-700', iconColor: 'text-emerald-500' },
    { label: 'Cancelled', key: 'cancelled', icon: XCircle, color: 'bg-red-50 text-red-600', iconColor: 'text-red-400' },
  ];

  return (
    <div className="space-y-6" data-testid="appointments-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Appointments</h1>
          <p className="text-sm text-slate-500 mt-1">Manage doctor appointments and schedules</p>
        </div>
        <div className="flex items-center gap-3">
          <Button className="bg-emerald-600 hover:bg-emerald-700" onClick={() => setShowBook(true)} data-testid="book-appointment-btn">
            <CalendarPlus size={16} className="mr-2" /> Book Appointment
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        {statCards.map(sc => {
          const active = sc.key === 'total' ? !filterStatus || filterStatus === 'all' : filterStatus === sc.key;
          return (
            <Card
              key={sc.key}
              className={`overflow-hidden cursor-pointer transition-all hover:shadow-md ${active ? 'ring-2 ring-emerald-500 shadow-md' : ''}`}
              onClick={() => setFilterStatus(sc.key === 'total' ? '' : (filterStatus === sc.key ? '' : sc.key))}
              data-testid={`stat-filter-${sc.key}`}
            >
              <CardContent className="p-4 flex items-center gap-3">
                <div className={`h-10 w-10 rounded-lg ${sc.color} flex items-center justify-center`}>
                  <sc.icon size={18} className={sc.iconColor} />
                </div>
                <div>
                  <p className="text-xl font-bold text-slate-900" data-testid={`stat-${sc.key}`}>{counts[sc.key] || 0}</p>
                  <p className="text-xs text-slate-500">{sc.label}</p>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <CardTitle className="text-lg">Appointment List</CardTitle>
            <div className="flex items-center gap-3">
              <Input type="date" value={date} onChange={e => setDate(e.target.value)} className="w-40" data-testid="filter-date" />
              <Select value={filterDoctor} onValueChange={setFilterDoctor}>
                <SelectTrigger className="w-44" data-testid="filter-doctor"><SelectValue placeholder="All Doctors" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Doctors</SelectItem>
                  {doctors.map(d => <SelectItem key={d.id} value={d.id}>{d.first_name} {d.last_name}</SelectItem>)}
                </SelectContent>
              </Select>
              <Select value={filterStatus} onValueChange={setFilterStatus}>
                <SelectTrigger className="w-36" data-testid="filter-status"><SelectValue placeholder="All Status" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  {['scheduled', 'checked_in', 'in_progress', 'completed', 'cancelled', 'no_show'].map(s => <SelectItem key={s} value={s}>{s.replace('_', ' ')}</SelectItem>)}
                </SelectContent>
              </Select>
              <Button variant="ghost" size="icon" onClick={fetch} data-testid="refresh-btn"><RefreshCw size={16} /></Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-12 text-slate-400">Loading...</div>
          ) : appointments.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-slate-400">
              <CalendarPlus size={40} className="mb-3 opacity-40" /><p>No appointments found</p>
            </div>
          ) : (
            <div className="border rounded-lg overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead className="font-semibold text-slate-700">Q#</TableHead>
                    <TableHead className="font-semibold text-slate-700">Time</TableHead>
                    <TableHead className="font-semibold text-slate-700">Patient</TableHead>
                    <TableHead className="font-semibold text-slate-700">Doctor</TableHead>
                    <TableHead className="font-semibold text-slate-700">Type</TableHead>
                    <TableHead className="font-semibold text-slate-700">Status</TableHead>
                    <TableHead className="font-semibold text-slate-700">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {appointments.map(apt => (
                    <TableRow key={apt.id} className="hover:bg-slate-50 transition-colors" data-testid={`appointment-row-${apt.id}`}>
                      <TableCell className="font-mono font-medium text-emerald-700">#{apt.queue_number}</TableCell>
                      <TableCell>
                        <span className="font-medium">{apt.time_slot}</span>
                        <span className="text-slate-400 text-xs ml-1">- {apt.slot_end_time}</span>
                      </TableCell>
                      <TableCell>
                        <div className="font-medium">{apt.patient_name}</div>
                        <div className="text-xs text-slate-400 font-mono">{apt.patient_code}</div>
                      </TableCell>
                      <TableCell>{apt.doctor_name}</TableCell>
                      <TableCell><Badge className={TYPE_COLORS[apt.appointment_type] || 'bg-slate-100'}>{apt.appointment_type?.replace('_', ' ')}</Badge></TableCell>
                      <TableCell><Badge className={STATUS_COLORS[apt.status]}>{apt.status?.replace('_', ' ')}</Badge></TableCell>
                      <TableCell><StatusActions apt={apt} onUpdate={handleStatusUpdate} onReschedule={setRescheduleApt} onUndo={handleUndo} isDoctor={isDoctor} /></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      <BookAppointmentDialog open={showBook} onOpenChange={setShowBook} doctors={doctors} onCreated={(bookedDate) => { if (bookedDate && bookedDate !== date) setDate(bookedDate); fetch(); }} />
      <ScheduleDialog open={showSchedule} onOpenChange={setShowSchedule} doctors={doctors} />
      <RescheduleDialog open={!!rescheduleApt} onOpenChange={() => setRescheduleApt(null)} apt={rescheduleApt} onDone={fetch} />
    </div>
  );
};

export default AppointmentsPage;
